import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Bell, CheckCircle2, AlertTriangle, Info, XCircle, Clock, Check
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
    } catch (e) {
      console.error("Error cargando notificaciones:", e);
      // No mostrar error al usuario, simplemente dejar la lista vacía
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? {...n, read: true} : n)
      );
    } catch (e) {
      console.error("Error marcando como leída:", e);
      // No mostrar error, la notificación se marcará cuando haya conexión
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("token");
      for (const n of notifications.filter(n => !n.read)) {
        await axios.post(`${API}/notifications/${n.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      toast.success("Todas las notificaciones marcadas como leídas");
    } catch (e) {
      console.error("Error marcando todas como leídas:", e);
      // Silenciar error
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle2 className="w-6 h-6 text-[#0ECB81]" />;
      case 'warning': return <AlertTriangle className="w-6 h-6 text-[#F0B90B]" />;
      case 'urgent': return <XCircle className="w-6 h-6 text-[#F6465D]" />;
      default: return <Info className="w-6 h-6 text-[#3B82F6]" />;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'success': return <Badge className="bg-[#0ECB81]/20 text-[#0ECB81]">Éxito</Badge>;
      case 'warning': return <Badge className="bg-[#F0B90B]/20 text-[#F0B90B]">Aviso</Badge>;
      case 'urgent': return <Badge className="bg-[#F6465D]/20 text-[#F6465D]">Urgente</Badge>;
      default: return <Badge className="bg-[#3B82F6]/20 text-[#3B82F6]">Info</Badge>;
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FCD535] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="border-l-4 border-[#FCD535] pl-4">
          <h1 className="text-2xl font-bold text-[#EAECEF] flex items-center gap-2">
            <Bell className="w-6 h-6 text-[#FCD535]" />
            Notificaciones
            {unreadCount > 0 && (
              <Badge className="bg-[#F6465D] text-white ml-2">{unreadCount} nuevas</Badge>
            )}
          </h1>
          <p className="text-[#848E9C]">Revisa tus mensajes y alertas</p>
        </div>
        
        {unreadCount > 0 && (
          <Button 
            onClick={markAllAsRead}
            className="bg-[#0ECB81] hover:bg-[#0ECB81]/80 text-white"
          >
            <Check className="w-4 h-4 mr-2" />
            Marcar todas como leídas
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {notifications.length === 0 ? (
          <Card className="card-dark">
            <CardContent className="p-12 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-[#848E9C] opacity-50" />
              <p className="text-[#848E9C] text-lg">No tienes notificaciones</p>
            </CardContent>
          </Card>
        ) : (
          notifications.map((notification) => (
            <Card 
              key={notification.id} 
              className={`card-dark transition-all ${
                !notification.read 
                  ? 'border-l-4 border-l-[#FCD535] bg-[#FCD535]/5' 
                  : 'opacity-70'
              }`}
            >
              <CardContent className="p-6">
                <div className="flex gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-1">
                    {getTypeIcon(notification.type)}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-bold text-[#EAECEF]">
                          {notification.title}
                        </h3>
                        {getTypeBadge(notification.type)}
                        {!notification.read && (
                          <Badge className="bg-[#FCD535] text-[#0B0E11]">Nueva</Badge>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-[#EAECEF] text-base leading-relaxed mb-4 whitespace-pre-wrap">
                      {notification.message}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-[#848E9C]">
                        <Clock className="w-4 h-4" />
                        {new Date(notification.created_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      
                      {!notification.read && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markAsRead(notification.id)}
                          className="border-[#2B3139] text-[#848E9C] hover:bg-[#2B3139]"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Marcar como leída
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
